import { useState } from "react";
import { Checkbox, Col, DatePicker, InputNumber, Radio, Row, Select, Space } from "antd";
import Paragraph from "antd/lib/typography/Paragraph";
import Text from "antd/lib/typography/Text";
import { CalendarOutlined, GlobalOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { TextService } from "../../Services/TextService";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { CalendarEvent } from "../../Types/CalendarEvent";
import { TimeChoice } from "../../Types/TimeChoice";
import { BookyInput, InputType } from "./BookyInput";
import { BookyCustomInput } from "./BookyCustomInput";
import { BookyTitleWithIcon } from "./TitleWithIcon";
import { GooglePlaceAutoComplete } from "../AutoComplete/GooglePlaceAutocomplete";
import { BookyWeekCalendar } from "../Calendar/WeekCalendar";
import { BookyEventList } from "../Calendar/BookyEventList";
import { EventTypeSelect } from "./EventTypeSelect";
import { EventSignUpSettings, EventWaitlistSettings } from "./EventSignUpSettings";
import { InfoTooltip } from "./InfoTooltip";
import { ReminderDaysInput } from "./ReminderDaysInput";
import { EventType } from "../../Types/EventType";
import { AnswerOptions } from "../../Types/AnswerOptions";

// The sections of the event form, shared by Skapa event and Ändra event so both pages look and read
// the same. Each page keeps its own state, saving and page-specific parts.

const GetText = TextService.GetText;

const UpgradeLink = (props: { text: string }) =>
    <span className='pro-link' style={{ justifyContent: 'left', display: 'flex' }}>
        <a href={"/" + getCurrentLanguage() + "/Pricing"}>{GetText("Common_UpgradeToPremium")}</a>&nbsp;{props.text}
    </span>;

/** Type, name, description and place. Must be inside the page's antd Form (fields name, eventDescription, eventLocation). */
export const EventDetailsFields = (props: {
    eventType: EventType,
    onEventTypeChange: (value: EventType) => void,
    defaultLocation?: string,
    onLocationSelected: (place: any) => void,
}) => <>
        <BookyCustomInput label={GetText("EventType_Question")}>
            <EventTypeSelect value={props.eventType} onChange={props.onEventTypeChange} />
        </BookyCustomInput>
        <BookyInput label={GetText("Create_Name")} name="name" requiredMessage={GetText("Create_Name_Required")} placeholder={GetText("Create_Name_Placeholder")} />
        <BookyInput label={GetText("Create_Description")} name="eventDescription" type={InputType.multiline} placeholder={GetText("Create_Description_Placeholder")} />
        <BookyCustomInput label={GetText("Create_Location")} name="eventLocation">
            {/* The key makes the field show the place once it has loaded on the edit page. */}
            <GooglePlaceAutoComplete key={props.defaultLocation ?? ""} defaultValue={props.defaultLocation} onPlaceSelected={props.onLocationSelected} />
        </BookyCustomInput>
    </>;

/** How long the event lasts and the calendar where the times are added. */
export const EventTimesFields = (props: {
    step: number,
    onStepChange: (step: number) => void,
    customStep: number,
    onCustomStepChange: (step: number) => void,
    /** Everything the calendar shows, for example also the admin's own Google events. */
    calendarEvents: CalendarEvent[],
    /** The event's own times, listed beside the calendar. */
    times: CalendarEvent[],
    startDate: string,
    onNavigate: (date: any) => void,
    CreateEventItem: (slot: any) => void,
    UpdateEvent: (slot: any) => void,
    DeleteEvent: (id: string) => void,
    SetFullDayOnEvent: (id: string, isFullDay: boolean) => void,
    show24hrsFormat: boolean,
    timeZone: string,
    timeZones: any[],
    onTimeZoneChange: (value: string) => void,
    /** Shown under the calendar, e.g. when no time has been chosen. */
    error?: string,
}) => {
    const [showTimeZone, setShowTimeZone] = useState(false);

    return <>
        <BookyTitleWithIcon text={GetText("Create_Times")}>
            <CalendarOutlined />
        </BookyTitleWithIcon>

        <Paragraph style={{ textAlign: "left", width: "100%", marginBottom: "10px", whiteSpace: "pre-line" }}>{GetText("Create_TimesHelp")}</Paragraph>

        <div style={{ textAlign: "left", width: "100%", marginBottom: "10px" }}><b>{GetText("Create_Duration")}</b></div>
        <BookyCustomInput>
            <Radio.Group value={props.step} onChange={(e) => props.onStepChange(e.target.value)} buttonStyle="solid">
                <Radio.Button value={30}>30 min</Radio.Button>
                <Radio.Button value={60}>60 min</Radio.Button>
                <Radio.Button value={-1}>{GetText("MyEventsEdit_CustomTime")}</Radio.Button>
                <Radio.Button value={-2}>{GetText("MyEventsEdit_AllDay")}</Radio.Button>
            </Radio.Group>
            {props.step === -1 ? <div style={{ width: "100%", textAlign: "left" }}>
                <InputNumber min={5} max={600} value={props.customStep} onChange={(value) => props.onCustomStepChange(value ?? 60)} addonAfter="min" />
            </div> : ""}
        </BookyCustomInput>

        <Row>
            <Col xs={24} md={20}>
                <BookyWeekCalendar startDate={props.startDate} onNavigate={props.onNavigate} DeleteEvent={props.DeleteEvent} events={props.calendarEvents} CreateEventItem={props.CreateEventItem} UpdateEvent={props.UpdateEvent} show24hrsFormat={props.show24hrsFormat} allDayStep={props.step === -2} />
            </Col>
            <Col xs={24} md={4}>
                <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                    <b>{GetText("Create_DateTimeTitle")} ({props.times.length})</b>
                </div>
                <BookyEventList events={props.times} onDeleteEvent={props.DeleteEvent} SetFullDayOnEvent={props.SetFullDayOnEvent} show24hrsFormat={props.show24hrsFormat} />
            </Col>
        </Row>

        {props.error ? <div style={{ textAlign: "left", width: "100%", marginTop: "5px" }}><Text data-testid="times-error" type="danger">{props.error}</Text></div> : ""}

        {/* Few need another time zone, so it stays out of the way until asked for. */}
        <div style={{ textAlign: "left", marginTop: "10px", width: "100%" }}>
            <Checkbox checked={showTimeZone} onChange={(e) => setShowTimeZone(e.target.checked)}><GlobalOutlined /> {GetText("Create_ShowTimezone")}</Checkbox>
            {showTimeZone ? <Select
                showSearch
                style={{ marginTop: "5px", width: "100%", maxWidth: "500px", display: "block" }}
                placeholder={GetText("Create_Timezone_Placeholder")}
                optionFilterProp="children"
                value={props.timeZone || undefined}
                onChange={props.onTimeZoneChange}
                filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                options={props.timeZones}
            /> : ""}
        </div>
    </>;
};

/** The settings section: answers, deadline, places, availability, participant list and reminder. */
export const EventSettingsFields = (props: {
    isPoll: boolean,
    isPremium: boolean,
    /** The answer options (form field answerOptions). */
    onAnswerOptionsChange?: (value: number) => void,
    /** The replies already given are read by the answer options, so they can't change once there are replies. */
    answerOptionsLocked?: boolean,
    deadline: string,
    onDeadlineChange: (value: string) => void,
    /** Shown under the deadline, e.g. when it has passed or comes after the first time. */
    deadlineError?: string,
    /** Days after it can't be chosen as deadline. */
    firstStart?: Date,
    maxParticipants: number,
    onMaxParticipantsChange: (value: number) => void,
    /** Once there are replies the max can't take a place from someone who already has one. */
    minParticipants?: number,
    membersOnly: boolean,
    setMembersOnly: (value: boolean) => void,
    showOnMemberSite: boolean,
    setShowOnMemberSite: (value: boolean) => void,
    allowWaitlist: boolean,
    setAllowWaitlist: (value: boolean) => void,
    maxWaitlist: number | null,
    setMaxWaitlist: (value: number | null) => void,
    timeChoice: TimeChoice,
    setTimeChoice: (value: TimeChoice) => void,
    timesCount: number,
    displayType: number,
    onDisplayTypeChange: (value: number) => void,
    automaticReminder: boolean,
    setAutomaticReminder: (value: boolean) => void,
    automaticReminderDays: number,
    setAutomaticReminderDays: (value: number) => void,
}) => {
    const minParticipants = props.minParticipants ?? 1;

    return <>
        <BookyTitleWithIcon text={GetText("Create_Settings")}>
            <SettingOutlined />
        </BookyTitleWithIcon>

        {/* A poll has yes/no/maybe. A sign-up has "Kommer" and "Kommer inte", or only "Kommer". */}
        {props.onAnswerOptionsChange ?
            <BookyCustomInput label={GetText("Create_Answers")} name="answerOptions" infoText={GetText(props.isPoll ? "Create_Answers_Placeholder" : "Create_AnswersSignUp_Placeholder")}
                helpText={props.answerOptionsLocked ? GetText("Create_AnswersLocked") : undefined}>
                <Radio.Group data-testid="answer-options" disabled={props.answerOptionsLocked} onChange={(e) => props.onAnswerOptionsChange!(e.target.value)}>
                    {props.isPoll ?
                        <Space direction="vertical">
                            <Radio value={AnswerOptions.YesNoMaybe}>{GetText("Create_YesNoMaybe")}</Radio>
                            <Radio value={AnswerOptions.YesNo}>{GetText("Create_YesNo")}</Radio>
                            <Radio value={AnswerOptions.Yes}>{GetText("Create_Yes")}</Radio>
                        </Space> :
                        <Space direction="vertical">
                            {/* Named by what the guest does. */}
                            <Radio data-testid="answer-options-yes-no" value={AnswerOptions.YesNo}>
                                {GetText("Create_ComingNotComing")}
                            </Radio>
                            <Radio data-testid="answer-options-yes" value={AnswerOptions.Yes}>
                                {GetText("Create_OnlyComing")}
                            </Radio>
                        </Space>}
                </Radio.Group>
            </BookyCustomInput> : ""}

        <BookyCustomInput label={GetText(props.isPoll ? "Create_DeadlinePoll" : "Create_Deadline")} infoText={GetText(props.isPoll ? "Create_DeadlinePoll_Placeholder" : "Create_Deadline_Placeholder")}>
            {props.isPremium ?
                <div style={{ textAlign: "left" }}>
                    <DatePicker placeholder={GetText("Create_Deadline_Set")} format={"YYYY-MM-DD HH:mm"} showTime={{ format: 'HH:mm' }}
                        value={props.deadline ? dayjs(props.deadline) : null}
                        status={props.deadlineError ? "error" : undefined}
                        disabledDate={(date: Dayjs) => date.isBefore(dayjs(), "day") || (props.firstStart != null && date.isAfter(dayjs(props.firstStart), "day"))}
                        onChange={(_date: Dayjs | null, dateString: string | string[]) => props.onDeadlineChange(dateString.toString())} />
                    {props.deadlineError ? <div><Text data-testid="deadline-error" type="danger">{props.deadlineError}</Text></div> : ""}
                </div> :
                <UpgradeLink text={GetText("Create_Deadline_Premium_SetDeadline")} />}
        </BookyCustomInput>

        <BookyCustomInput label={GetText("MembersOnly_Label")}>
            {/* A poll can't be fully booked, so places and waitlist are only for a sign-up. They come first. */}
            {props.isPoll ? "" : <div style={{ textAlign: "left", marginBottom: "6px" }}>
                <div>
                    <InputNumber min={minParticipants} max={props.isPremium ? 9999 : 25} value={props.maxParticipants} onChange={(value) => props.onMaxParticipantsChange(value ?? minParticipants)} style={{ width: "90px" }} />
                    <span style={{ marginLeft: "8px" }}>{GetText("Create_GuestsMax")}</span>
                    <InfoTooltip text={GetText("Create_Guests_Premium_RestrictNumberOfGuests")} />
                </div>
                {!props.isPremium ? <UpgradeLink text={GetText("Create_Guests_Premium_ToAllowMoreGuests")} /> : ""}
                {minParticipants > 1 ?
                    <Paragraph style={{ fontSize: "80%", marginBottom: 0 }}>{GetText("MyEventsEdit_MaxParticipantsAtLeastBooked").replace("{0}", minParticipants.toString())}</Paragraph> : ""}
                <EventWaitlistSettings allowWaitlist={props.allowWaitlist} setAllowWaitlist={props.setAllowWaitlist} maxWaitlist={props.maxWaitlist} setMaxWaitlist={props.setMaxWaitlist} />
            </div>}
            <EventSignUpSettings membersOnly={props.membersOnly} setMembersOnly={props.setMembersOnly} showOnMemberSite={props.showOnMemberSite} setShowOnMemberSite={props.setShowOnMemberSite}
                timeChoice={props.timeChoice} setTimeChoice={props.setTimeChoice} isPoll={props.isPoll} timesCount={props.timesCount} />
        </BookyCustomInput>

        <BookyCustomInput label={GetText("Create_Visibility")} infoText={GetText("Create_Visibility_Placeholder")}>
            <div style={{ textAlign: "left" }}>
                {/* DisplayType 0 shows the list to participants, 1 hides it. */}
                <Checkbox checked={props.displayType === 0} onChange={(e) => props.onDisplayTypeChange(e.target.checked ? 0 : 1)}>{GetText("Create_Visibility_ShowParticipants")}</Checkbox>
            </div>
        </BookyCustomInput>

        <BookyCustomInput label={GetText("Create_AutomaticReminder")} infoText={GetText("Create_AutomaticReminder_Placeholder")}>
            {props.isPremium ?
                <div style={{ textAlign: "left" }}>
                    <Checkbox checked={props.automaticReminder} onChange={(e) => props.setAutomaticReminder(e.target.checked)}>{GetText("Create_AutomaticReminder_Send")}</Checkbox>
                    {props.automaticReminder ? <div><ReminderDaysInput days={props.automaticReminderDays} onChange={props.setAutomaticReminderDays} /></div> : null}
                </div> :
                <UpgradeLink text={GetText("Create_AutomaticReminder_Premium_ToSendAutomaticReminder")} />}
        </BookyCustomInput>
    </>;
};
